import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

/**
 * Read-only list of the real, admin-managed catalog products (db.deals)
 * linked to the logged-in broker's vendor. Shared by broker/Products.jsx
 * and broker/Profiles.jsx — both surface the same vendor deal list.
 */
export default function BrokerDealsTable() {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api
            .get("/broker/deals")
            .then((r) => setDeals(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    if (!loading && deals.length === 0) {
        return (
            <div className="rounded-2xl bg-white border border-navy-100/70 py-16 text-center">
                <img
                    src="/panda/panda-checklist.png"
                    alt=""
                    className="h-32 mx-auto mb-4"
                />
                <div className="font-display text-2xl text-navy-700 mb-1">
                    No linked products yet
                </div>
                <div className="text-navy-500 text-sm">
                    Perx hasn't linked any catalogue products to your vendor.
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
            <Table data-testid="broker-deals-table">
                <TableHeader>
                    <TableRow className="bg-cream hover:bg-cream">
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Product
                        </TableHead>
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Member price
                        </TableHead>
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Savings
                        </TableHead>
                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                            Status
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {deals.map((d) => {
                        const symbol = d.is_paid_USD ? "US$" : "$";
                        const active = d.status === "active";
                        return (
                            <TableRow key={d.id} data-testid={`broker-deal-row-${d.id}`}>
                                <TableCell className="font-medium text-navy-700">
                                    {d.title}
                                </TableCell>
                                <TableCell className="text-sm">
                                    {symbol}
                                    {d.price?.toFixed(2)}
                                    {d.original_price > d.price && (
                                        <span className="text-navy-400 line-through ml-2">
                                            {symbol}
                                            {d.original_price?.toFixed(2)}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="text-gold-600 font-medium">
                                    {d.savings_pct}%
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        className={
                                            (active
                                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : "bg-navy-50 text-navy-500 border-navy-100") +
                                            " border text-[10px] uppercase tracking-widest"
                                        }
                                    >
                                        {active ? "Active" : "Inactive"}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}
